'use client';

import {
  type UseQueryResult,
  type UserPortfolioSnapshotWire,
  useAuth,
} from '@morfi/backend-sdk-v2/client';
import { D6_SCALE, getUsdcMint } from '@/config/solana';
import { useUserPortfolioSnapshotQuerySelect } from '@/hooks/positions/user-portfolio/query-select';
import { useActiveNetwork } from '@/lib/network/use-active-network';

export interface TradeBalances {
  readonly usdcBalance: number;
  readonly usdcRawBalance: string;
  readonly outcomeBalance: number;
  readonly outcomeRawBalance: string;
  readonly claimRightsBalance: number;
  readonly claimRightsRawBalance: string;
}

export type TradeBalancesQueryResult = UseQueryResult<TradeBalances | undefined, Error> & {
  readonly usdcBalance: number | undefined;
};

/**
 * Trade-form balances for one wallet, selected from the viewer's indexed
 * portfolio snapshot. The app shell owns that snapshot and keeps it live over
 * NATS, so opening a market performs no Solana RPC account reads; the on-chain
 * instruction remains the authority if the index lags a just-landed transfer.
 *
 * `claimRightsMint` only signals a bonding-curve pool: claim rights are read
 * from the outcome's indexed position row.
 */
export function useTradeBalances(
  walletAddress: string | undefined,
  outcomeMint: string | undefined,
  claimRightsMint?: string,
): TradeBalancesQueryResult {
  const { user } = useAuth();
  const quoteMint = getUsdcMint(useActiveNetwork());
  const withClaims = claimRightsMint !== undefined;
  const select = (snapshot: UserPortfolioSnapshotWire | null): TradeBalances | undefined =>
    snapshot === null || walletAddress === undefined
      ? undefined
      : selectTradeBalances(snapshot, walletAddress, quoteMint, outcomeMint, withClaims);
  const query = useUserPortfolioSnapshotQuerySelect(user?.id ?? '', select);
  return { ...query, usdcBalance: query.data?.usdcBalance };
}

function selectTradeBalances(
  snapshot: UserPortfolioSnapshotWire,
  wallet: string,
  quoteMint: string,
  outcomeMint: string | undefined,
  withClaims: boolean,
): TradeBalances {
  // A wallet without an indexed row holds none of that asset.
  const usdcRawBalance =
    snapshot.quoteBalances.find((row) => row.wallet === wallet && row.quoteMint === quoteMint)
      ?.balanceD6 ?? '0';
  const position =
    outcomeMint === undefined
      ? undefined
      : snapshot.positions.find((row) => row.wallet === wallet && row.outcomeMint === outcomeMint);
  const outcomeRawBalance = position?.balanceD6 ?? '0';
  const claimRightsRawBalance = withClaims ? (position?.claimRightsBalanceD6 ?? '0') : '0';
  return {
    usdcBalance: Number(BigInt(usdcRawBalance)) / D6_SCALE,
    usdcRawBalance,
    outcomeBalance: Number(BigInt(outcomeRawBalance)) / D6_SCALE,
    outcomeRawBalance,
    claimRightsBalance: Number(BigInt(claimRightsRawBalance)) / D6_SCALE,
    claimRightsRawBalance,
  };
}
